import React, { useEffect, useState } from "react";
import { client } from "@/lib/api";

// WW-208 / WW-209 / WW-210 · desk metrics on analytics (test accounts left out server side).
const card = "rounded-xl border border-neutral-200 bg-white p-4";
const h3 = "mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500";

function useGet(url) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    client.get(url).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [url]);
  return { d, err };
}

function ReviewTime() {
  const { d, err } = useGet("/admin/analytics/review-time");
  return (
    <section data-testid="panel-review-time" className={card}>
      <h3 className={h3}>review time · queue to published</h3>
      {err && <p data-testid="review-time-error" className="text-sm text-red-600">couldn't load</p>}
      {!d && !err && <p className="text-sm text-neutral-400">loading…</p>}
      {d && (
        <>
          <p className="text-sm" data-testid="review-time-waiting">
            upcoming waiting over 48 hours: <b data-testid="review-time-waiting-count">{d.waiting_over_48h}</b> of <b data-testid="review-time-waiting-total">{d.upcoming_waiting}</b>
            <span className="text-neutral-500"> · {d.past_still_pending_over_48h} past, still pending</span>
          </p>
          <table className="mt-3 w-full text-sm" data-testid="review-time-weeks">
            <thead><tr className="text-left text-neutral-500"><th>week from</th><th>published</th><th>with queue time</th><th>median hours</th></tr></thead>
            <tbody>
              {d.weeks.map((w) => (
                <tr key={w.week_start} data-testid={`review-time-week-${w.week_start}`}>
                  <td>{w.week_start}</td><td data-testid={`review-time-published-${w.week_start}`}>{w.published}</td><td data-testid={`review-time-timed-${w.week_start}`}>{w.with_queue_time}</td><td>{w.median_hours ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-4 mb-1 text-[11px] uppercase tracking-[0.14em] text-neutral-500">slowest 10 published this week</p>
          {d.slowest_this_week.length ? (
            <ul className="divide-y divide-neutral-200" data-testid="review-time-slowest">
              {d.slowest_this_week.map((r) => (
                <li key={r.id} data-testid={`review-time-slow-${r.id}`} className="flex justify-between gap-3 py-1 text-sm">
                  <span className="truncate">{r.title}</span>
                  <span className="shrink-0 text-neutral-600">{r.hours} h · {r.reviewed_by}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs text-neutral-400">nothing published this week with a queue time</p>}
          <p className="mt-3 text-[11px] text-neutral-400" data-testid="review-time-blank">
            {d.events_without_queue_time} published events have no queue time and are left out of the median
            {d.events_without_publish_time ? ` · ${d.events_without_publish_time} have no recorded publish time and are in no week` : ""}
          </p>
        </>
      )}
    </section>
  );
}

function ComeBack() {
  const { d, err } = useGet("/admin/analytics/come-back");
  return (
    <section data-testid="panel-come-back" className={card}>
      <h3 className={h3}>come back rate · action in 3 of first 4 weeks</h3>
      {err && <p className="text-sm text-red-600">couldn't load</p>}
      {!d && !err && <p className="text-sm text-neutral-400">loading…</p>}
      {d && (
        <>
          <table className="w-full text-sm" data-testid="come-back-table">
            <thead><tr className="text-left text-neutral-500"><th>signed up week</th><th>people</th><th>came back</th></tr></thead>
            <tbody>
              {d.cohorts.map((c) => (
                <tr key={c.week_start} data-testid={`come-back-week-${c.week_start}`}>
                  <td>{c.week_start}</td><td>{c.signed_up}</td>
                  <td data-testid={`come-back-share-${c.week_start}`}>
                    {c.state === "ready" ? (c.share == null ? "—" : `${(c.share * 100).toFixed(0)}% (${c.came_back})`) : c.state}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-[11px] text-neutral-400" data-testid="come-back-tracking">
            actions counted: going, interested, save, i went, review, share to squad · tracked from {d.tracking_from ? d.tracking_from.slice(0, 10) : "the first action"}
          </p>
        </>
      )}
    </section>
  );
}

function Curators() {
  const { d, err } = useGet("/admin/analytics/curators-month");
  return (
    <section data-testid="panel-curators-month" className={card}>
      <h3 className={h3}>curators who listed or edited this month</h3>
      {err && <p className="text-sm text-red-600">couldn't load</p>}
      {!d && !err && <p className="text-sm text-neutral-400">loading…</p>}
      {d && (
        <>
          <p className="text-2xl font-bold" data-testid="curators-month-count">{d.count}</p>
          {d.curators.length ? (
            <ul className="mt-2 divide-y divide-neutral-200" data-testid="curators-month-list">
              {d.curators.map((c) => (
                <li key={c.user_id} data-testid={`curators-month-row-${c.user_id}`} className="flex justify-between py-1 text-sm">
                  <span>{c.name}</span><span className="text-neutral-600">{c.created} listed · {c.updated} edited</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs text-neutral-400">none yet this month</p>}
          <p className="mt-3 text-[11px] text-neutral-400" data-testid="curators-month-unknown">
            {d.events_with_unknown_role} older events have no known lister and are not counted
          </p>
        </>
      )}
    </section>
  );
}

export const DeskMetrics = () => (
  <div data-testid="desk-metrics" className="mb-8 grid gap-4 lg:grid-cols-3">
    <ReviewTime />
    <ComeBack />
    <Curators />
  </div>
);
